<template>
  <div
    ref="noteResultContainerRef"
    :class="[ui.root, canEdit ? '' : ui.locked, isFocusedWithin || isHovered ? ui.focused : '']"
  >
    <div :class="ui.headerWrapper">
      <div :class="[ui.headerTitle]">
        <div :class="[ui.headerTitleAvatars, note.created_by && note.call_id ? 'min-w-11.5' : '']">
          <AwAvatar
            v-if="note.created_by"
            size="sm"
            :text="`${note.created_by.first_name?.[0] ?? ''}${note.created_by.last_name?.[0] ?? ''}`"
          />
          <AwAvatar
            v-if="note.call_id"
            size="sm"
            text=""
            :icon="isAIComputing ? 'i-ph-sparkle-fill' : 'i-ph-magic-wand-fill'"
            icon-class="text-purple-500"
            :class="[ui.avatarAI.base, note.created_by ? ui.avatarAI.position : '']"
          />
        </div>

        <div :class="[ui.titleWrapper, shimmerClassComputed]">
          <span v-if="isCallCurrentlyOngoing" :class="ui.title">{{ $t("notes.result.callOngoing") }}</span>
          <span v-else-if="isNoteGenerating || (isFakeNote && !fakeCallIsOngoing)" :class="ui.title">{{
            $t("notes.result.callSummaryGenerating")
          }}</span>
          <span v-else-if="isTitleGenerating" :class="ui.title">{{ $t("notes.result.titleGenerating") }}</span>
          <textarea
            v-else
            :value="title"
            :rows="1"
            :placeholder="$t('notes.result.titlePlaceholder')"
            :disabled="!canEdit"
            :class="ui.title"
            @input="onTitleInput"
            @keydown.enter.prevent
          ></textarea>
        </div>

        <div>
          <AwDropdownMenu
            v-if="canEdit"
            :items="[
              {
                label: $t('common.actions.delete'),
                icon: 'i-ph-trash-duotone',
                color: 'critical',
                onClick: deleteNote,
              },
            ]"
          >
            <AwButton variant="ghost" color="secondary" size="sm" leading-icon="i-ph-dots-three-bold" />
          </AwDropdownMenu>
        </div>
      </div>

      <div :class="ui.subtitleWrapper">
        <AwTooltip :disabled="canEdit">
          <template #content>
            <span class="flex flex-nowrap">{{ $t("notes.result.lockedTooltip") }}</span>
          </template>
          <AwIcon v-if="!canEdit" name="i-ph-lock-key-duotone" class="text-gray-700" />
        </AwTooltip>

        <span :class="ui.subtitle">{{ subtitleLabel }}</span>
      </div>

      <!-- Restrict Badges to notes linked to a call -->
      <div v-if="note.call_id" :class="ui.tagWrapper">
        <AwBadge
          v-for="(tag, index) in tags"
          :key="`${tag}-${index}`"
          size="sm"
          class="rounded-full"
          :color="tagColor(tag)"
          :is-removable="canEdit"
          :label="tagLabel(tag)"
          @remove="updateTags(tags.filter((t) => t !== tag))"
        />
        <AwDropdownMenu v-if="possibleTags.length > 0 && canEdit" :items="possibleTags">
          <AwBadge
            size="sm"
            icon="i-ph-plus-bold"
            :label="tags.length ? undefined : $t('notes.result.addTag')"
            class="rounded-full!"
            color="secondary"
          />
        </AwDropdownMenu>
      </div>
    </div>

    <AwTabs v-if="tabItems.length > 1" v-model="activeTab" :items="tabItems" />
    <div v-else class="divider"></div>

    <div :class="ui.contentWrapper">
      <template v-if="(note.call_id && !note.summarized_content) || isFakeNote">
        <TipTapEditor
          v-if="!isFakeNote"
          :class="{ 'call-note-editor': isCallNoteBeingWritten }"
          :content="noteContent"
          :editable="canEditContent"
          :autofocus="shouldFocusCallNote"
          :placeholder="isCallNoteBeingWritten ? $t('notes.callNote.placeholder') : undefined"
          @update:content="updateNoteContent"
        />
        <div v-if="isFakeNote || isCallCurrentlyOngoing || isNoteGenerating" :class="ui.emptyWrapper">
          <AwSkeleton v-if="isFakeNote" :class="ui.skeleton" />
          <span :class="[ui.skeletonText, 'mt-2']">{{ $t("notes.callNote.summaryHint") }}</span>

          <template v-if="isCallCurrentlyOngoing">
            <div class="mt-2 cursor-pointer" @click="isReferenceTemplateOpen = !isReferenceTemplateOpen">
              <AwBanner
                :text="$t('notes.result.qualificationTemplateBanner')"
                icon="i-ph-lightbulb-duotone"
                color="secondary"
                class="!bg-gray-200"
              >
                <template #action>
                  <AwButton
                    variant="ghost"
                    color="secondary"
                    size="sm"
                    leading-icon="i-ph-caret-down-bold"
                    :class="['transition-transform', isReferenceTemplateOpen && 'rotate-180']"
                    @click.stop="isReferenceTemplateOpen = !isReferenceTemplateOpen"
                  />
                </template>
              </AwBanner>
            </div>
            <div
              v-if="isReferenceTemplateOpen"
              class="flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-200 p-4"
            >
              <div v-for="section in qualificationReferenceTemplate" :key="section.title">
                <p class="font-semibold text-md pb-1">{{ section.title }}</p>
                <ul class="list-disc pl-5 text-md text-gray-800">
                  <li v-for="item in section.items" :key="item">{{ item }}</li>
                </ul>
              </div>
            </div>
          </template>
        </div>
      </template>

      <template v-else-if="activeTab === 'notes'">
        <div>
          <TipTapEditor :content="noteContent" :editable="canEditContent" @update:content="updateNoteContent" />
        </div>
      </template>

      <template v-else-if="activeTab === 'summary'">
        <div v-if="isSummarizing" :class="ui.emptyWrapper">
          <AwSkeleton :class="ui.skeleton" />
          <span :class="[ui.skeletonText, shimmerClassComputed]">{{ $t("notes.result.analyzingConversation") }}</span>
        </div>
        <div v-else>
          <TipTapEditor :content="summarizedContent" :editable="canEdit" @update:content="updateSummarizedContent" />
        </div>
      </template>

      <template v-else-if="activeTab === 'transcription'">
        <div>
          <TipTapEditor :content="props.note.audio_transcription" :editable="false" />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { NoteNotFoundError, useNotes } from "~/components/note/composables/useNotes";
import type { DropdownMenuItem } from "@asap/design/components/AwDropdownMenu.vue";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { useCall } from "~/components/call/composables/useCall";
import { useAutosave } from "~/composables/useAutosave";
import type { Note, NoteUpdateBody } from "@asap/shared/schemas/note/crud";
import { noteUpdateBodySchema } from "@asap/shared/schemas/note/crud";
import { useUserMe } from "~/components/user/composables/useUser";
import { useManagees } from "~/components/team-member/composables/useManagees";
import { useToast } from "~/composables/useToast";
import { getFullName } from "@asap/shared/utils/people";
import { translateTalentCallType } from "@asap/shared/utils/translate";
import { talentCallTypeSchema, type TalentCallType } from "@asap/shared/schemas/call.schema";
import { useFocusWithin, useElementHover } from "@vueuse/core";
import { qualificationReferenceTemplate } from "./qualification-reference-template";
import { hasNoteText } from "./note.model";

const emit = defineEmits<{ onDelete: []; onRecreate: [note: Note] }>();

const props = defineProps<{
  note: Note;
  isFakeNote?: boolean;
  fakeCallIsOngoing?: boolean;
  linkedCallIsOngoing?: boolean;
}>();

const ui = {
  root: "rounded-xl border-1 border-gray-400 p-4 flex flex-col gap-3",
  locked: "bg-gray-50",
  focused: "border-1 border-secondary-600",

  contentWrapper: "",
  headerWrapper: "flex flex-col gap-2 w-full",
  headerTitle: "flex items-center gap-1",
  headerTitleAvatars: "relative",
  subtitleWrapper: "flex items-center gap-1 w-full",
  tagWrapper: "flex flex-wrap gap-2",
  emptyWrapper: "flex flex-col gap-2",

  avatarAI: {
    base: "bg-purple-100 border-purple-50 border-1",
    position: "absolute top-0 left-5",
  },
  titleWrapper: "min-w-0 w-full flex",
  title: [
    "min-w-0 w-full resize-none border-0 bg-transparent p-0 font-bold text-[16px] leading-normal outline-none",
    "[field-sizing:content] placeholder:text-gray-500 disabled:cursor-default disabled:bg-transparent",
  ],
  subtitle: "text-gray-800 text-sm",
  skeleton: "flex max-w-80 h-4",
  skeletonText: "text-gray-600",
};

const TALENT_CALL_TYPE_COLORS: Record<TalentCallType, "primary" | "success" | "yellow" | "purple" | "secondary"> = {
  qualification: "primary",
  mission_proposal: "success",
  mission_follow_up: "yellow",
  timesheet_reminder: "yellow",
  talent_support: "purple",
  whats_up: "success",
  other: "secondary",
};

// Tags written before our call types (SipSim labels, removed types) are shown as is.
const tagLabel = (tag: string): string => {
  const talentCallType = talentCallTypeSchema.safeParse(tag).data;
  return talentCallType ? translateTalentCallType.fr[talentCallType] : tag;
};

const tagColor = (tag: string) => {
  const talentCallType = talentCallTypeSchema.safeParse(tag).data;
  return talentCallType ? TALENT_CALL_TYPE_COLORS[talentCallType] : "secondary";
};

const TAG_OPTIONS: (DropdownMenuItem & { value: string })[] = talentCallTypeSchema.options.map((talentCallType) => ({
  label: translateTalentCallType.fr[talentCallType],
  value: talentCallType,
  onClick: () => {
    updateTags([...tags.value, talentCallType]);
  },
}));

const { updateNote, removeNote, createNote, summarizeNoteContent, generateNoteTitle } = useNotes();
const { getCall, updateCall } = useCall();
const { teamMember } = useUserMe();
const { managees } = useManagees();
const { addToastError } = useToast();
const { t } = useI18n();

const noteResultContainerRef = ref<HTMLElement | null>(null);
const { focused: isFocusedWithin } = useFocusWithin(noteResultContainerRef);
const isHovered = useElementHover(noteResultContainerRef);

// The end of an unanswered call deletes its blank note: what the consultant writes next recreates it.
let isDeletedByConsultant = false;
const saveNote = async (patch: NoteUpdateBody) => {
  try {
    return await updateNote(props.note.id, patch);
  } catch (error) {
    if (!(error instanceof NoteNotFoundError)) throw error;
    if (isDeletedByConsultant || !props.note.call_id || !patch.content) {
      addToastError({ description: t("notes.errors.update") });
      throw error;
    }

    const recreatedNote = await createNote({
      workspace_id: props.note.workspace_id,
      talent_id: props.note.talent_id,
      call_id: props.note.call_id,
      content: patch.content,
    });
    emit("onRecreate", recreatedNote);
    return recreatedNote;
  }
};

const autosave = useAutosave({
  save: saveNote,
  draftBackup: {
    storageKey: () => `note-draft:${props.note.id}`,
    patchSchema: noteUpdateBodySchema,
  },
});

const activeTab = ref<"notes" | "summary" | "transcription">("notes");
const isReferenceTemplateOpen = ref(false);
const isCallOngoing = ref<boolean>(false);
const isTitleGenerating = ref<boolean>(false);
const isSummarizing = ref<boolean>(false);
const tags = ref<string[]>([]);
const summarizedContent = ref<string | null>(props.note.summarized_content);
const noteContent = ref<string | null>(props.note.content);
const title = ref<string | null>(props.note.title);

const hasAudio = computed(() => !!props.note.audio_transcription);
// Settled when the note loads or its call summary arrives, so clearing the notes does not drop the tab being edited.
const hasOriginalNotes = ref(hasNoteText(props.note.content));

const tabItems = computed(() => {
  const items = [];

  if (summarizedContent.value || isSummarizing.value) {
    items.push({ label: t("notes.result.tabs.summary"), value: "summary", icon: "i-ph-sparkle-duotone" });
  }
  if (hasAudio.value) {
    items.push({ label: t("notes.result.tabs.transcription"), value: "transcription", icon: "i-ph-file-duotone" });
  }
  if (hasOriginalNotes.value) {
    items.push({ label: t("notes.result.tabs.notes"), value: "notes", icon: "i-ph-list-bullets-duotone" });
  }

  return items;
});

const subtitleLabel = computed(() => {
  const createdLabel = props.note.created_by
    ? t("notes.result.subtitle.createdBy", { name: getFullName(props.note.created_by) })
    : t("notes.result.subtitle.created");
  const summarizedLabel = props.note.call_id ? t("notes.result.subtitle.summarized") : null;
  const agoLabel = t("notes.result.subtitle.ago", {
    duration: formatDistanceToNow(new Date(props.note.created_at), { locale: fr }),
  });

  return [createdLabel, summarizedLabel, agoLabel].filter(Boolean).join(" ");
});

const isNoteGenerating = computed(() => {
  return props.note.call_id && isCallOngoing.value === false && !summarizedContent.value && title.value === null;
});

const isCallCurrentlyOngoing = computed(() => {
  return isCallOngoing.value || (props.isFakeNote === true && props.fakeCallIsOngoing === true);
});

const shimmerClassComputed = computed(() => {
  if (
    isCallOngoing.value ||
    isNoteGenerating.value ||
    isTitleGenerating.value ||
    isSummarizing.value ||
    props.isFakeNote === true
  ) {
    return "shimmer-effect";
  }
  return "";
});

const isAIComputing = computed(() => {
  return isCallOngoing.value || isNoteGenerating.value || isTitleGenerating.value || isSummarizing.value;
});

const possibleTags = computed(() => {
  if (!tags.value || tags.value.length === 0) {
    return TAG_OPTIONS;
  }

  const options = TAG_OPTIONS.filter((tag) => !tags.value.includes(tag.value));
  return options;
});

const isOwnedByMeOrManagee = computed(() => {
  if (props.note.created_by?.id === teamMember.value?.auth_user_id) {
    return true;
  }
  return managees.value.some((managee) => managee.auth_user_id === props.note.created_by?.id);
});

const canEdit = computed(() => {
  if (isCallOngoing.value || isNoteGenerating.value) {
    return false;
  }
  return isOwnedByMeOrManagee.value;
});

// The consultant writes in the call note while the call runs and its summary is generated.
const canEditContent = computed(() => isOwnedByMeOrManagee.value);

// The rest of the card stays locked meanwhile: the content reads as a field to write in.
const isCallNoteBeingWritten = computed(
  () => canEditContent.value && (isCallCurrentlyOngoing.value || Boolean(isNoteGenerating.value))
);

// Focus the call note as it appears, unless the consultant is already typing elsewhere.
const shouldFocusCallNote =
  props.linkedCallIsOngoing === true && (!document.activeElement || document.activeElement === document.body);

const updateTags = async (newTags: string[]) => {
  if (!props.note.call_id) return;

  tags.value = newTags;

  await updateCall(props.note.call_id, { tags: newTags });
};

const updateTitle = (newTitle?: string | null) => {
  if (!canEdit.value) return;
  title.value = newTitle ?? "";
  autosave.queue({ title: newTitle });
};

const onTitleInput = (event: Event) => {
  if (event.target instanceof HTMLTextAreaElement) updateTitle(event.target.value);
};

const updateSummarizedContent = (newContent?: string | null) => {
  if (!canEdit.value) return;

  summarizedContent.value = newContent ?? "";
  autosave.queue({ summarized_content: newContent });
};

const updateNoteContent = (newContent?: string | null) => {
  if (!canEditContent.value) return;

  noteContent.value = newContent ?? "";
  autosave.queue({ content: newContent });
};

const deleteNote = () => {
  isDeletedByConsultant = true;
  removeNote(props.note.id);
  emit("onDelete");
};

const summarizeContent = async () => {
  if (!canEdit.value) return;
  if (!props.note.audio_transcription) return;
  if (props.note.summarized_content) return;
  isSummarizing.value = true;
  try {
    const summarized = await summarizeNoteContent({
      content: props.note.content,
      audio_transcription: props.note.audio_transcription,
      note_id: props.note.id,
    });

    title.value = summarized.title;
    summarizedContent.value = summarized.summary;

    autosave.queue({ summarized_content: summarizedContent.value, title: title.value });
  } catch (error) {
    addToastError({ description: t("notes.result.errors.summarize") }, error);
  } finally {
    isSummarizing.value = false;
  }
};

const generateTitle = async (content: string) => {
  if (!canEdit.value) return;

  isTitleGenerating.value = true;

  try {
    title.value = await generateNoteTitle(content);
    autosave.queue({ title: title.value });
  } catch (error) {
    addToastError({ description: t("notes.result.errors.generateTitle") }, error);
  } finally {
    isTitleGenerating.value = false;
  }
};

watch(
  () => props.linkedCallIsOngoing,
  async (linkedCallIsOngoing) => {
    if (linkedCallIsOngoing !== undefined) {
      isCallOngoing.value = linkedCallIsOngoing;
      return;
    }

    // The notes page stopped tracking the call, possibly before seeing it end: read its state again.
    if (isCallOngoing.value && props.note.call_id) {
      const call = await getCall(props.note.call_id);
      if (call) {
        isCallOngoing.value = call.ongoing;
      }
    }
  }
);

// A call note is created empty at call start: its summary, title and call tags arrive later.
watch(
  () => props.note.summarized_content,
  async (incomingSummarizedContent) => {
    if (incomingSummarizedContent && !summarizedContent.value) {
      // A call is summarized once it has ended.
      isCallOngoing.value = false;
      summarizedContent.value = incomingSummarizedContent;
      hasOriginalNotes.value = hasNoteText(noteContent.value);
      activeTab.value = "summary";

      if (props.note.call_id) {
        const call = await getCall(props.note.call_id);
        if (call) {
          tags.value = call.tags;
        }
      }
    }
  }
);
watch(
  () => props.note.title,
  (incomingTitle) => {
    if (incomingTitle && !title.value) {
      title.value = incomingTitle;
    }
  }
);

onMounted(async () => {
  if (props.note.call_id) {
    const call = await getCall(props.note.call_id);
    if (call) {
      tags.value = call.tags;
      isCallOngoing.value = call.ongoing;
      activeTab.value = "summary";
    }
  }

  if (hasAudio.value && !props.note.call_id) {
    activeTab.value = "summary";
    if (!props.note.summarized_content) {
      await summarizeContent();
    }
  } else if (!title.value && summarizedContent.value) {
    await generateTitle(summarizedContent.value);
  } else if (!title.value && noteContent.value && !props.note.call_id) {
    // The API titles a call note from its summary, or as an unanswered call.
    await generateTitle(noteContent.value);
  }
});
</script>

<style scoped lang="css">
.call-note-editor {
  background-color: white;
  border: 1px solid var(--gray-4);
  border-radius: 12px;
  padding: 12px;
}

.call-note-editor:focus-within {
  border-color: var(--blue);
  outline: 1px solid var(--blue);
}

.call-note-editor :deep(.tiptap) {
  min-height: 120px;
}

.shimmer-effect {
  display: inline-block;
  background: var(--gray-4) linear-gradient(to right, var(--gray-4) 0%, #ffffff 50%, var(--gray-4) 100%);
  background-position: -4rem top;
  background-repeat: no-repeat;
  background-clip: text;
  -webkit-background-clip: text;
  text-fill-color: transparent;
  -webkit-text-fill-color: transparent;
  animation: shimmer 2.2s cubic-bezier(0.4, 0, 0.9, 1) infinite;
  background-size: 4rem 100%;
}

@keyframes shimmer {
  0% {
    background-position: -20% top;
  }
  100% {
    background-position: 120% top;
  }
}

.divider {
  height: 1px;
  width: 100%;
  background: var(--gray-5);
}
</style>
